import { DecimalPipe } from '@angular/common';
import { endOfDay } from '@core/date.facade';
import { U2bDatePickerButtonBarComponent } from '@u2bUi/date-picker/u2b-date-picker-button-bar.component';
import { U2B_DATE_PICKER_CUSTOM_TIME_PT } from '@u2bUi/date-picker/u2b-date-picker.pt';
import { U2bDatePickerApplyDirective } from '@u2bUi/directives/u2b-date-picker-apply.directive';
import { ChangeDetectionStrategy, Component, computed, effect, input, model, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { Button } from 'primeng/button';
import { DatePicker } from 'primeng/datepicker';
import { Dialog } from 'primeng/dialog';
import { InputNumber } from 'primeng/inputnumber';
import { Select } from 'primeng/select';

import { TranslationModule } from '@core/translation/translation.module';
import { BcmService } from '@modules/bcm/bcm.service';
import { ElectricMeterProductKey } from '@shared/models/electric-meter-product-type';
import { U2bDateMaskDirective } from '@u2bUi/directives/u2b-date-mask.directive';
import { U2bDialogComponent } from '@u2bUi/dialog/u2b-dialog.component';
import { U2bIconButtonComponent } from '@u2bUi/icon-button/u2b-icon-button.component';

import { BookingV2ElectricityProductOption, BookingV2ElectricMeterAssignment } from '../../../dtos/booking-v2.dtos';

export type BookingV2EMReadingMode = 'intermediate' | 'disconnect' | 'editStart';

export interface BookingV2EMReadingResult {
    mode: BookingV2EMReadingMode;
    endDate?: Date | null;
    endMeterReading?: number | null;
    startDate?: Date | null;
    startMeterReading?: number | null;
    productId?: number | null;
}

// PrimeNG port of the legacy ElectricMeterReadingDialog / ReadingDialog (editStart):
// captures the reading for an interim reading, a disconnect (Trennen), or a start
// correction. Provider-aware — for web-switchable meters the value is read live by
// the backend, so manual entry is hidden. Pure UI: the parent runs the API call.
@Component({
    selector: 'booking-v2-electric-meter-reading-dialog',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        DecimalPipe, FormsModule, Button, DatePicker, Dialog, InputNumber, Select, TranslationModule,
        U2bDateMaskDirective, U2bDatePickerApplyDirective, U2bDatePickerButtonBarComponent,
        U2bDialogComponent, U2bIconButtonComponent,
    ],
    templateUrl: './electric-meter-reading-dialog.component.html',
})
export class BookingV2ElectricMeterReadingDialogComponent {

    readonly mode = input.required<BookingV2EMReadingMode>();
    readonly assignment = input.required<BookingV2ElectricMeterAssignment>();
    // Live value for web-switchable meters (fetched by the parent tab).
    readonly liveReading = input<number | null>(null);
    readonly products = input<BookingV2ElectricityProductOption[]>([]);

    readonly visible = model<boolean>(false);
    readonly confirmed = output<BookingV2EMReadingResult>();

    readonly customTimePt = U2B_DATE_PICKER_CUSTOM_TIME_PT;
    readonly today = new Date();
    readonly readingDateMax = endOfDay(this.today);

    readonly currentLocale = BcmService.currentLocale;

    readonly endDate = signal<Date | null>(null);
    readonly endMeterReading = signal<number | null>(null);
    readonly startDate = signal<Date | null>(null);
    readonly startMeterReading = signal<number | null>(null);
    readonly productId = signal<number | null>(null);

    readonly isApiMeter = computed(() => this.assignment()?.provider?.switchableViaWeb === true);

    readonly title = computed(() => {
        switch (this.mode()) {
            case 'intermediate': return 'bookingV2.advanced.power.action.intermediateTitle';
            case 'disconnect': return 'bookingV2.advanced.power.action.disconnectTitle';
            default: return 'bookingV2.advanced.power.action.editStartTitle';
        }
    });

    // Usage-billed meters offer the billing product when read out / disconnected.
    readonly showProductSelect = computed(() =>
        this.mode() !== 'editStart'
        && this.assignment()?.productTypeKey === ElectricMeterProductKey.BY_USAGE,
    );

    readonly canSave = computed(() => {
        if (this.mode() === 'editStart') {
            return this.startDate() !== null && this.startMeterReading() !== null;
        }
        if (this.isApiMeter()) {
            // Disconnect reads the value live server-side; interim needs the fetched one.
            return this.mode() === 'disconnect' || this.liveReading() !== null;
        }
        return this.endDate() !== null && this.endMeterReading() !== null;
    });

    constructor() {
        // Re-seed the form each time the dialog opens.
        effect(() => {
            if (!this.visible()) {
                return;
            }
            const a = this.assignment();
            this.endDate.set(this.today);
            this.endMeterReading.set(this.isApiMeter() ? this.liveReading() : null);
            this.startDate.set(a?.from ?? this.today);
            this.startMeterReading.set(a?.meterReadingStart ?? null);
            this.productId.set(a?.productId ?? null);
        });
    }

    save(): void {
        if (!this.canSave()) {
            return;
        }
        const mode = this.mode();
        if (mode === 'editStart') {
            this.confirmed.emit({
                mode,
                startDate: this.startDate(),
                startMeterReading: this.startMeterReading(),
            });
        } else {
            this.confirmed.emit({
                mode,
                endDate: this.endDate(),
                // API meters: the backend reads the live value, so send the fetched one.
                endMeterReading: this.isApiMeter() ? this.liveReading() : this.endMeterReading(),
                productId: this.showProductSelect() ? this.productId() : null,
            });
        }
        this.visible.set(false);
    }

    cancel(): void {
        this.visible.set(false);
    }
}
